import { ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export function toKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Build a 6x7 grid of Date objects (Monday-first) for the given month
function buildGrid(viewDate) {
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const startOffset = (first.getDay() + 6) % 7; // Mon=0
  const start = new Date(first);
  start.setDate(first.getDate() - startOffset);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

/**
 * Shared month calendar grid.
 * props: viewDate (Date), onPrev, onNext, selectedKey, onSelectDay(dateKey),
 *        countByDate (map key->number), accent ("light"|"dark")
 */
export function MonthCalendar({ viewDate, onPrev, onNext, selectedKey, onSelectDay, countByDate = {}, dark = false }) {
  const days = buildGrid(viewDate);
  const todayKey = toKey(new Date());
  const month = viewDate.getMonth();

  const base = dark ? "text-white" : "text-[#0A0A0A]";
  const muted = dark ? "text-[#6b6b6b]" : "text-[#c4c4c4]";
  const cellBorder = dark ? "border-[#2a2a2a]" : "border-[#e5e5e5]";

  return (
    <div className={`${base} font-body`} data-testid="month-calendar">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-2xl capitalize">
          {MONTHS[month]} {viewDate.getFullYear()}
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={onPrev} data-testid="cal-prev" aria-label="Mês anterior"
            className={`h-9 w-9 flex items-center justify-center rounded-full border ${cellBorder} hover:border-[#E20074] transition-colors`}>
            <ChevronLeft size={18} />
          </button>
          <button onClick={onNext} data-testid="cal-next" aria-label="Mês seguinte"
            className={`h-9 w-9 flex items-center justify-center rounded-full border ${cellBorder} hover:border-[#E20074] transition-colors`}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {WEEKDAYS.map((w) => (
          <div key={w} className={`text-center text-xs font-medium ${dark ? "text-[#a3a3a3]" : "text-[#525252]"}`}>{w}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {days.map((d, i) => {
          const key = toKey(d);
          const inMonth = d.getMonth() === month;
          const isSunday = d.getDay() === 0;
          const isPast = key < todayKey;
          const disabled = isSunday || isPast || !inMonth;
          const selected = key === selectedKey;
          const isToday = key === todayKey;
          const count = countByDate[key] || 0;

          return (
            <button
              key={i}
              disabled={disabled}
              onClick={() => !disabled && onSelectDay(key)}
              data-testid={`cal-day-${key}`}
              className={[
                "relative aspect-square rounded-lg border flex flex-col items-center justify-center text-sm transition-colors",
                cellBorder,
                selected ? "bg-[#E20074] border-[#E20074] text-white"
                  : disabled ? `${muted} cursor-not-allowed opacity-60`
                  : `${base} hover:border-[#E20074] cursor-pointer`,
                isToday && !selected ? "ring-1 ring-[#00B4D8]" : "",
              ].join(" ")}
            >
              <span className={!inMonth ? "opacity-40" : ""}>{d.getDate()}</span>
              {count > 0 && !isSunday && (
                <span className={`absolute bottom-1 h-1.5 w-1.5 rounded-full ${selected ? "bg-white" : "bg-[#E20074]"}`} />
              )}
              {isSunday && inMonth && (
                <span className={`text-[9px] leading-none mt-0.5 ${selected ? "text-white/80" : dark ? "text-[#6b6b6b]" : "text-[#c4c4c4]"}`}>fechado</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { MONTHS };
